import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { api, onUnauthorized, tokens } from '@/lib/api'
import type { AdminUser, TokenPair } from '@/types'

interface AuthState {
  user: AdminUser | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => void
  canApprove: boolean
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null)
  const [loading, setLoading] = useState(true)

  const signOut = useCallback(() => {
    tokens.clear()
    setUser(null)
  }, [])

  useEffect(() => onUnauthorized(signOut), [signOut])

  useEffect(() => {
    if (!tokens.access) {
      setLoading(false)
      return
    }
    api
      .get<AdminUser>('/auth/me')
      .then(setUser)
      .catch(() => tokens.clear())
      .finally(() => setLoading(false))
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const pair = await api.post<TokenPair>('/auth/login', { email, password })
    tokens.set(pair)
    setUser(await api.get<AdminUser>('/auth/me'))
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      signIn,
      signOut,
      canApprove: user?.role === 'owner' || user?.role === 'admin',
    }),
    [user, loading, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}
